import { type LiveBreakWidgetCopy, twitchChannelUrl } from "@loot-os/api-contract";
import chevronDownIcon from "@loot-os/icons/icons/chevron-down";
import expandIcon from "@loot-os/icons/icons/expand";
import externalLinkIcon from "@loot-os/icons/icons/external-link";
import eyeIcon from "@loot-os/icons/icons/eye";
import eyeOffIcon from "@loot-os/icons/icons/eye-off";
import minusIcon from "@loot-os/icons/icons/minus";
import pipIcon from "@loot-os/icons/icons/pip";
import { useState } from "react";

import { Avatar } from "../../core/Avatar.js";
import { Button } from "../../core/Button.js";
import { buttonStyles } from "../../core/Button.styles.js";
import { Icon } from "../../core/Icon.js";
import { IconButton } from "../../core/IconButton.js";
import { Menu, MenuItem, MenuRadioGroup, MenuRadioItem, MenuSeparator } from "../../core/Menu.js";
import { Skeleton } from "../../core/Skeleton.js";
import { Text } from "../../core/Text.js";
import { AppLink } from "../../foundations/AppLink.js";
import { cn } from "../../foundations/cn.js";
import { FOCUS_RING } from "../../foundations/interaction.js";
import { useLayout } from "../../foundations/themeContext.js";
import {
  hasStreamChoices,
  type LiveBreakWidgetPickerSections,
  type LiveBreakWidgetStream,
  widgetSurface,
} from "./liveBreakWidget.js";

export interface LiveBreakWidgetStreamRowProps {
  /** The live break (sponsored, or pinned from picture-in-picture) or the promoted stream it shows. */
  stream: LiveBreakWidgetStream;
  words: LiveBreakWidgetCopy;
  /**
   * The picker's groups (`widgetPickerSections`): with more than one stream to choose, the row's
   * left side opens it, upward, the shown stream checked.
   */
  picker?: LiveBreakWidgetPickerSections | null;
  /** A pick from the picker: a live break (the app pins it) or a promoted stream (it selects it). */
  onPick?: (stream: LiveBreakWidgetStream) => void;
  /** View → Picture-in-picture. Left out, the menu offers Watch on Twitch and Hide widget alone. */
  onOpenPip?: (stream: LiveBreakWidgetStream) => void;
  /** View → Hide widget: the stream row hides until picture-in-picture opens again. */
  onHide?: () => void;
  /** Folds the row to its pill; left out, the row has no collapse button. */
  onCollapse?: () => void;
  /** A menu open from the start, for a story. */
  defaultOpenMenu?: "view" | "picker";
  className?: string;
}

/**
 * The stream's line under its name: the live break's phase, or Live on Twitch, or loading. One
 * line from md; on a phone, beside the row's buttons, it wraps to a second rather than lose its
 * noun.
 */
function StreamLine({
  stream,
  words,
  recent,
  compact,
}: {
  stream: LiveBreakWidgetStream;
  words: LiveBreakWidgetCopy;
  recent: boolean;
  compact: boolean;
}) {
  const className = compact ? "truncate" : "max-md:line-clamp-2 md:truncate";
  if (!stream.liveBreak) {
    return (
      <Text as="span" variant="body-b3" tone="secondary" className={className}>
        {words.liveOnTwitch}
      </Text>
    );
  }
  const { phase } = stream.liveBreak;
  if (phase !== null) {
    return (
      <Text as="span" variant="body-b3" tone="secondary" className={className}>
        {words.phase[phase]}
      </Text>
    );
  }
  if (recent) {
    return (
      <Text as="span" variant="body-b3" tone="secondary" className={className}>
        {words.picker.liveBreak}
      </Text>
    );
  }
  return (
    <span className="flex h-4 items-center">
      <Skeleton className="h-3 w-28 rounded-sm" />
      <span className="sr-only">{words.loading}</span>
    </span>
  );
}

/** The avatar, the name with its live dot, and the line under it; `compact` in the picker. */
function StreamIdentity({
  stream,
  words,
  live = true,
  recent = false,
  compact = false,
}: {
  stream: LiveBreakWidgetStream;
  words: LiveBreakWidgetCopy;
  live?: boolean;
  recent?: boolean;
  compact?: boolean;
}) {
  return (
    <>
      <Avatar
        src={stream.avatarSrc}
        name={stream.name}
        size={compact ? "sm" : "md"}
        decorative
        outlined
        className={compact ? undefined : "max-md:size-9"}
      />
      <span
        className={cn(
          "flex flex-1 flex-col text-left",
          compact ? "min-w-0" : "min-w-24 md:min-w-32",
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <Text
            as="span"
            variant={compact ? "body-b2-strong" : "body-b1-strong"}
            tone="primary"
            className={cn("truncate", !compact && "max-md:text-body-b2-strong")}
          >
            {stream.name}
          </Text>
          {live && (
            <>
              <span aria-hidden className="size-2 shrink-0 rounded-pill bg-status-danger" />
              <span className="sr-only">{words.live}</span>
            </>
          )}
        </span>
        <StreamLine stream={stream} words={words} recent={recent} compact={compact} />
      </span>
    </>
  );
}

/** A thumb's size on a phone: the row's buttons are 40 px below md, 32 px from it. */
const PHONE_TARGET = "max-md:h-control-md";

/** The row's left side as the picker's trigger: Sponsored, then Recent, opening upward. */
function StreamPicker({
  stream,
  words,
  picker,
  onPick,
  defaultOpen,
}: {
  stream: LiveBreakWidgetStream;
  words: LiveBreakWidgetCopy;
  picker: LiveBreakWidgetPickerSections;
  onPick?: (stream: LiveBreakWidgetStream) => void;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const pick = (key: string) => {
    const picked = [...picker.sponsored, ...picker.recent].find((entry) => entry.key === key);
    if (picked) onPick?.(picked);
  };
  // A list long enough to scroll fades out at the bottom, so its cut reads as more to come; the
  // padding under it lets the last row scroll clear of the fade.
  const scrolls = picker.sponsored.length + picker.recent.length > 3;
  return (
    <Menu
      side="top"
      align="start"
      open={open}
      onOpenChange={setOpen}
      className={cn(
        "max-h-48 min-w-64",
        scrolls &&
          "pb-6 [mask-image:linear-gradient(to_bottom,black_calc(100%-1.5rem),transparent)]",
      )}
      trigger={
        <button
          type="button"
          className={cn(
            "-my-1.5 -ml-3 flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-l-xl py-1.5 pr-3 pl-3",
            "max-md:-my-1 max-md:-ml-2.5 max-md:py-1 max-md:pl-2.5",
            "hover:bg-bg-hover data-popup-open:bg-bg-hover",
            FOCUS_RING,
          )}
        >
          <StreamIdentity stream={stream} words={words} />
          <Icon icon={expandIcon} size={16} className="shrink-0 text-text-tertiary" />
          <span className="sr-only">{words.picker.hint}</span>
        </button>
      }
    >
      {picker.sponsored.length > 0 && (
        <MenuRadioGroup label={words.picker.sponsored} value={stream.key} onValueChange={pick}>
          {picker.sponsored.map((entry) => (
            <MenuRadioItem key={entry.key} value={entry.key} closeOnClick={false}>
              <StreamIdentity stream={entry} words={words} compact />
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      )}
      {picker.sponsored.length > 0 && picker.recent.length > 0 && <MenuSeparator />}
      {picker.recent.length > 0 && (
        <MenuRadioGroup label={words.picker.recent} value={stream.key} onValueChange={pick}>
          {picker.recent.map((entry) => (
            <MenuRadioItem key={entry.key} value={entry.key} closeOnClick={false}>
              <StreamIdentity stream={entry} words={words} live={false} recent compact />
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      )}
    </Menu>
  );
}

/**
 * The widget's stream row: a live break's host on Twitch (sponsored, or the one last opened in
 * picture-in-picture) with its stage's phase, Join and the View menu, or a promoted stream, live
 * on Twitch with no live break, with View alone. With more than one stream to choose, its left
 * side opens the stream picker, marked by an up-and-down chevron. On a phone its buttons are 40 px
 * and View is its eye alone.
 */
export function LiveBreakWidgetStreamRow({
  stream,
  words,
  picker,
  onPick,
  onOpenPip,
  onHide,
  onCollapse,
  defaultOpenMenu,
  className,
}: LiveBreakWidgetStreamRowProps) {
  const layout = useLayout();
  const choices = hasStreamChoices(picker);
  return (
    <div
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-xl py-1.5 pr-2 pl-3",
        "max-md:py-1 max-md:pr-1.5 max-md:pl-2.5",
        widgetSurface(layout.chrome),
        className,
      )}
    >
      {choices && picker ? (
        <>
          <StreamPicker
            stream={stream}
            words={words}
            picker={picker}
            onPick={onPick}
            defaultOpen={defaultOpenMenu === "picker"}
          />
          {stream.liveBreak && (
            <span aria-hidden className="-mr-2 h-8 w-px shrink-0 self-center bg-border-subtle" />
          )}
        </>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-3">
          <StreamIdentity stream={stream} words={words} />
        </span>
      )}
      <span className="ml-2 flex shrink-0 items-center gap-1.5 md:ml-4">
        {stream.liveBreak && (
          <AppLink
            href={stream.liveBreak.href}
            aria-label={words.joinLabel(stream.name)}
            className={buttonStyles({ variant: "primary", size: "sm", className: PHONE_TARGET })}
          >
            {words.join}
          </AppLink>
        )}
        <Menu
          side="top"
          align="end"
          defaultOpen={defaultOpenMenu === "view"}
          trigger={
            // On a phone, the eye alone (named as ever), so the phase line keeps its room.
            <Button
              variant="secondary"
              size="sm"
              aria-label={words.viewLabel(stream.name)}
              trailing={<Icon icon={chevronDownIcon} size={16} className="max-md:hidden" />}
              className="max-md:size-control-md max-md:px-0"
            >
              <Icon icon={eyeIcon} size={18} className="md:hidden" />
              <span className="max-md:hidden">{words.view}</span>
            </Button>
          }
        >
          {onOpenPip && (
            <MenuItem icon={pipIcon} onClick={() => onOpenPip(stream)}>
              {words.pictureInPicture}
            </MenuItem>
          )}
          <MenuItem
            icon={externalLinkIcon}
            href={twitchChannelUrl(stream.channel)}
            render={
              <a
                href={twitchChannelUrl(stream.channel)}
                target="_blank"
                rel="noopener noreferrer"
              />
            }
          >
            {words.watchOnTwitch}
            <span className="sr-only">{words.newTab}</span>
          </MenuItem>
          {onHide && (
            <MenuItem icon={eyeOffIcon} onClick={onHide}>
              {words.hide}
            </MenuItem>
          )}
        </Menu>
        {onCollapse && (
          <span data-widget-collapse className="flex">
            {/* Minus, not a chevron: folding the widget never looks like View's menu beside it. */}
            <IconButton
              icon={minusIcon}
              label={words.collapse(stream.name)}
              variant="ghost"
              size="sm"
              onClick={onCollapse}
              className="max-md:size-control-md"
            />
          </span>
        )}
      </span>
    </div>
  );
}
